import type { SetPasswordDetails } from '@/scopes/auth/domain/types'
import type { InvisibleRecaptchaHandle } from '@/scopes/common/components/InvisibleRecaptcha/InvisibleRecaptcha'
import { Button } from '@sunwebgroup/uds-ui/button'
import { useRef } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from '@/intl'
import { useSetPasswordActions } from '@/scopes/auth/infrastructure/setPasswordDependencies'
import { useSetPasswordStore } from '@/scopes/auth/infrastructure/useSetPasswordStore'
import { getIsSubmitDisabled } from '@/scopes/auth/pages/selectors/setPasswordSelectors'
import { InvisibleRecaptcha } from '@/scopes/common/components/InvisibleRecaptcha/InvisibleRecaptcha'
import { AuthErrorBanner } from '../AuthErrorBanner'
import { AuthFormLayout } from '../AuthFormLayout'
import { NewPasswordInput } from '../password/NewPasswordInput'
import { SetPasswordConfirmation } from './SetPasswordConfirmation'
import styles from './SetPasswordForm.module.css'

type SetPasswordFormValues = Omit<SetPasswordDetails, 'recaptchaToken'>

const DEFAULT_FORM_VALUES: SetPasswordFormValues = { password: '' }

export function SetPasswordForm() {
  const intl = useIntl()
  const methods = useForm<SetPasswordFormValues>({ defaultValues: DEFAULT_FORM_VALUES })
  const { setPasswordRequested } = useSetPasswordActions()
  const submitStatus = useSetPasswordStore(state => state.submitStatus)
  const hasSubmitError = useSetPasswordStore(state => state.submitError !== null)
  const isSubmitDisabled = useSetPasswordStore(getIsSubmitDisabled)
  const recaptchaRef = useRef<InvisibleRecaptchaHandle>(null)

  async function handleSubmit(values: SetPasswordFormValues) {
    const recaptchaToken = await recaptchaRef.current?.execute()

    return setPasswordRequested({ ...values, recaptchaToken })
  }

  const isLoadingStatus = submitStatus === 'loading'

  if (submitStatus === 'success')
    return <SetPasswordConfirmation />

  return (
    <FormProvider {...methods}>
      <AuthFormLayout
        onSubmit={methods.handleSubmit(handleSubmit)}
        title={intl.formatMessage({ id: 'auth_set_password_form_title' })}
        subtitle={intl.formatMessage({ id: 'auth_set_password_form_subtitle' })}
        dataQa="auth-set-password-form"
      >
        <InvisibleRecaptcha ref={recaptchaRef} />
        <div className={styles.fields}>
          {hasSubmitError && <AuthErrorBanner message={intl.formatMessage({ id: 'auth_set_password_form_error_generic' })} />}
          <NewPasswordInput name="password" disabled={isLoadingStatus} />
        </div>
        <div className={styles.footer}>
          <Button type="submit" variant="primary" fullWidth isLoading={isLoadingStatus} disabled={isSubmitDisabled}>
            {intl.formatMessage({ id: 'auth_set_password_form_submit_label' })}
          </Button>
        </div>
      </AuthFormLayout>
    </FormProvider>
  )
}
